import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect, useState } from "react";
import { CalendarDays, RotateCw } from "lucide-react";
import styled from "styled-components";
import { GD } from "src/GD";
import { AccountingCard as Card, KpiLabel, ReserveRefreshButton } from "./styled";
import { formatReserveAmount, parseRollingReservePeriod, type RollingReservePeriodCurrency } from "./rollingReserveData";
import { useTranslation } from "react-i18next";
import { parseRollingPeriodReport, type RollingPeriodReport } from "./rollingPeriodReport";
import { RollingPeriodReportContent } from "./RollingPeriodReportContent";

const CurrencyMetrics = styled.div`
	display: grid;
	flex: 1;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
	gap: 12px;
	margin-top: 16px;
`;

const PeriodCurrency = styled.section`
	min-width: 0;
	padding: 12px 14px;
	border-radius: 10px;
	background: #f5f3ff;
	h4 { margin: 0 0 12px; color: #667085; font-size: 12px; font-weight: 400; }
	dl { margin: 0; display: flex; flex-direction: column; gap: 8px; }
	dl > div { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 4px 12px; }
	dt { color: #667085; font-size: 12px; }
	dd { margin: 0; max-width: 100%; font-size: 18px; font-weight: 600; color: #1a1d26; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
`;

interface RollingReservePeriodCardProps {
	clientId?: number;
	projectId: number;
	userId: number;
	from: string;
	to: string;
	channelFiltered?: boolean;
}

export const RollingReservePeriodCard = ({ clientId, projectId, userId, from, to, channelFiltered = false }: RollingReservePeriodCardProps) => {
	useLocale();
	const { t } = useTranslation();
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<{ busy: boolean; rows?: RollingReservePeriodCurrency[]; report?: RollingPeriodReport; error?: string }>({ busy: true });

	useEffect(() => {
		let cancelled = false;
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => { cancelled = true; setState({ busy: false }); });
		setState({ busy: true });
		const load = async () => {
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (cancelled || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
			};
			try {
				await checkContext();
				// Global Accountant uses project totals without a client_id; client cards keep their endpoint.
				const response = await GD.REQ_API.request({
					method: clientId === undefined ? "rollings/period" : "rollings/get", apiVersion: 2, expectedProjectId: projectId,
					headers: { ignoreBusy: "true" }, data: { ...(clientId !== undefined ? { client_id: clientId } : {}), from, to },
				});
				await checkContext();
				if (response?.error || response?.data === undefined || response.data === null) throw new Error(msg("periodReserveRequestFailed"));
				if (clientId === undefined) {
					const report = parseRollingPeriodReport(response.data, from, to);
					if (!cancelled) setState({ busy: false, report });
				} else {
					const rows = parseRollingReservePeriod(response.data, clientId, from, to);
					if (!cancelled) setState({ busy: false, rows });
				}
			} catch {
				if (!cancelled) setState({ busy: false, error: t("accountant.rollingError") });
			}
		};
		void load();
		return () => { cancelled = true; GD.S_AUTH_LOG_OUT.unsubscribe(subscription); };
	}, [clientId, projectId, userId, from, to, attempt, t]);

	return <Card style={{ height: "auto", minWidth: 0 }} role="region" aria-label={t("accountant.rollingTitle")} aria-busy={state.busy}>
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
			<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
				<div style={{ padding: 10, borderRadius: 12, color: "#6d5ce8", background: "#f1effd", display: "flex" }}><CalendarDays size={20} /></div>
				<div>
					<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 10px" }}>
						<div style={{ fontSize: 14, fontWeight: 600, color: "#1a1d26" }}>{t("accountant.rollingTitle")}</div>
						{state.report?.coverage.partial && <span style={{ padding: "1px 7px", borderRadius: 5, color: "#8a5700", background: "#fff9ed", fontSize: 12, fontWeight: 500, lineHeight: 1.5 }}>{t("accountant.rollingPartialBadge")}</span>}
					</div>
					<KpiLabel>{from} → {to}</KpiLabel>
					{channelFiltered && <KpiLabel style={{ display: "block" }}>{msg("reserveCoversAllChannels")}</KpiLabel>}
				</div>
			</div>
			<ReserveRefreshButton $busy={state.busy} title={state.error ? msg("retry") : msg("refresh")} aria-label={state.error ? msg("retry") : msg("refresh")} disabled={state.busy} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
		</div>
		<div role="status" aria-live="polite">
			{state.busy && <KpiLabel style={{ display: "block", marginTop: 12 }}>{t("accountant.rollingLoading")}</KpiLabel>}
			{state.error && <div style={{ marginTop: 12, color: "#b42332", fontSize: 13 }}>{state.error}</div>}
			{state.rows?.length === 0 && <KpiLabel style={{ display: "block", marginTop: 12 }}>{t("accountant.rollingEmpty")}</KpiLabel>}
		</div>
		{state.report && <RollingPeriodReportContent report={state.report} />}
		{!!state.rows?.length && <CurrencyMetrics>
			{state.rows.map(row => <PeriodCurrency key={row.currency} aria-label={t("accountant.reserveAria", { currency: row.currency })}>
				<h4>{row.currency}</h4>
				<dl>
					<div><dt>{t("accountant.reserved")}</dt><dd>{formatReserveAmount(row.taken)}</dd></div>
					<div><dt>{t("accountant.released")}</dt><dd>{formatReserveAmount(row.released)}</dd></div>
					<div><dt>{t("accountant.held")}</dt><dd>{formatReserveAmount(row.held)}</dd></div>
				</dl>
			</PeriodCurrency>)}
		</CurrencyMetrics>}
	</Card>;
};
